import type { Job } from "@malfak/contracts";
import { Ban, FileDown, Pause, Play, RotateCcw } from "lucide-react";
import { buttonVariants, Button, IconButton } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Tooltip } from "@/components/ui/overlays";
import { routeUrl } from "@/lib/api/client";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useJobAction } from "./use-jobs";

/** Cancel / pause / resume / retry / failure report for one job, as offered by `job.can`. */
export function JobControls({ job, compact = false, className }: { job: Job; compact?: boolean; className?: string }) {
  const { t } = useT();
  const confirm = useConfirm();
  const cancel = useJobAction("cancelJob");
  const pause = useJobAction("pauseJob");
  const resume = useJobAction("resumeJob");
  const retry = useJobAction("retryJob");
  const params = { params: { jobId: job.id }, body: {} } as const;
  const onCancel = async () => {
    const ok = await confirm({ title: t("jobs.confirmCancel.title"), description: t("jobs.confirmCancel.body"), confirmLabel: t("jobs.confirmCancel.confirm"), cancelLabel: t("jobs.confirmCancel.keep"), danger: true });
    if (ok) cancel.mutate(params);
  };
  const reportHref = routeUrl("jobReport", { params: { jobId: job.id } });
  const retryLabel = job.failed > 0 ? t("jobs.actions.retryFailed") : t("jobs.actions.retry");

  if (compact) {
    return (
      <div className={cn("flex shrink-0 items-center gap-0.5", className)}>
        {job.can.pause && (
          <IconButton size="icon-xs" label={t("jobs.actions.pause")} onClick={() => pause.mutate(params)} disabled={pause.isPending} data-testid="job-pause">
            <Pause />
          </IconButton>
        )}
        {job.can.resume && (
          <IconButton size="icon-xs" label={t("jobs.actions.resume")} onClick={() => resume.mutate(params)} disabled={resume.isPending} data-testid="job-resume">
            <Play className="flip-rtl" />
          </IconButton>
        )}
        {job.can.cancel && (
          <IconButton size="icon-xs" label={t("jobs.actions.cancel")} onClick={() => void onCancel()} disabled={cancel.isPending} data-testid="job-cancel" className="hover:text-bad">
            <Ban />
          </IconButton>
        )}
        {job.can.retry && (
          <IconButton size="icon-xs" label={retryLabel} onClick={() => retry.mutate(params)} disabled={retry.isPending} data-testid="job-retry">
            <RotateCcw />
          </IconButton>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {job.can.pause && (
        <Button size="xs" variant="outline" icon={<Pause />} loading={pause.isPending} onClick={() => pause.mutate(params)} data-testid="job-pause">
          {t("jobs.actions.pause")}
        </Button>
      )}
      {job.can.resume && (
        <Button size="xs" variant="primary" icon={<Play className="flip-rtl" />} loading={resume.isPending} onClick={() => resume.mutate(params)} data-testid="job-resume">
          {t("jobs.actions.resume")}
        </Button>
      )}
      {job.can.cancel && (
        <Button size="xs" variant="ghost" className="text-bad hover:bg-bad-soft hover:text-bad" icon={<Ban />} loading={cancel.isPending} onClick={() => void onCancel()} data-testid="job-cancel">
          {t("jobs.actions.cancel")}
        </Button>
      )}
      {job.can.retry && (
        <Button size="xs" variant="primary" icon={<RotateCcw />} loading={retry.isPending} onClick={() => retry.mutate(params)} data-testid="job-retry">
          {retryLabel}
        </Button>
      )}
      {job.can.report && (
        <Tooltip content={t("jobs.actions.report")}>
          <a href={reportHref} download={`job-${job.id}-failures.csv`} className={cn(buttonVariants({ variant: "ghost", size: "xs" }))} data-testid="job-report">
            <FileDown aria-hidden />
            {t("jobs.actions.report")}
          </a>
        </Tooltip>
      )}
    </div>
  );
}
